import { c as _c } from "react/compiler-runtime";
import { useDropLinkPreviewToggleControl } from "@/components/waves/drops/useDropLinkPreviewToggleControl";
import DropPartMarkdownWithPropLogger from "@/components/drops/view/part/DropPartMarkdownWithPropLogger";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import type { ApiDropGroupMention } from "@/generated/models/ApiDropGroupMention";
import type { ApiDropPart } from "@/generated/models/ApiDropPart";
import type { ApiDropReferencedNFT } from "@/generated/models/ApiDropReferencedNFT";
import type { ApiMentionedWave } from "@/generated/models/ApiMentionedWave";
import type { ApiWaveMin } from "@/generated/models/ApiWaveMin";
import React from "react";
import { useAuth } from "@/components/auth/Auth";
import QuorumProposalCompactContent from "@/components/waves/quorum/QuorumProposalCompactContent";
import { parseQuorumProposalMarkdown } from "@/components/waves/quorum/quorumProposalMarkdown";
import type { DropContentPresentation } from "./dropContentPresentation";
import EditDropLexical from "./EditDropLexical";
import WaveDropQuoteWithDropId from "./WaveDropQuoteWithDropId";
import { ApiDropType } from "@/generated/models/ApiDropType";
import type { ApiWaveMinWithChatLinkSettings } from "@/helpers/waves/wave.helpers";
import { CHAT_LINK_RESTRICTION_MESSAGE, areHandlesEqual, isChatLinkRestrictionApplicable } from "@/helpers/waves/chat-link-restriction.helpers";
interface WaveDropPartContentMarkdownProps {
  readonly mentionedUsers: Array<ApiDropMentionedUser>;
  readonly mentionedGroups?: Array<ApiDropGroupMention> | undefined;
  readonly mentionedWaves: Array<ApiMentionedWave>;
  readonly referencedNfts: Array<ApiDropReferencedNFT>;
  readonly part: ApiDropPart;
  readonly wave: ApiWaveMin;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly isEditing?: boolean | undefined;
  readonly isSaving?: boolean | undefined;
  readonly onSave?: ((newContent: string, mentions?: ApiDropMentionedUser[], mentionedGroups?: ApiDropGroupMention[], mentionedWaves?: ApiMentionedWave[]) => void) | undefined;
  readonly onCancel?: (() => void) | undefined;
  readonly drop?: ApiDrop | undefined; // Add drop to check for edited status
  readonly onLinkCardActionsActiveChange?: ((href: string, active: boolean) => void) | undefined;
  readonly contentPresentation?: DropContentPresentation | undefined;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
}
const WaveDropPartContentMarkdown: React.FC<WaveDropPartContentMarkdownProps> = t0 => {
  const $ = _c(63);
  const {
    mentionedUsers,
    mentionedGroups: t1,
    mentionedWaves,
    referencedNfts,
    part,
    wave,
    onQuoteClick,
    isEditing: t2,
    isSaving: t3,
    onSave,
    onCancel,
    drop,
    onLinkCardActionsActiveChange,
    contentPresentation: t4,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth
  } = t0;
  let t5;
  if ($[0] !== t1) {
    t5 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const mentionedGroups = t5;
  const isEditing = t2 === undefined ? false : t2;
  const isSaving = t3 === undefined ? false : t3;
  const contentPresentation = t4 === undefined ? "default" : t4;
  const {
    connectedProfile
  } = useAuth();
  const linkPreviewToggleControl = useDropLinkPreviewToggleControl(drop);
  const dropId = drop?.id;
  const dropSerialNo = drop?.serial_no;
  const waveId = wave.id;
  let t6;
  bb0: {
    let t7;
    if ($[2] !== embedPath) {
      t7 = embedPath ? [...embedPath] : [];
      $[2] = embedPath;
      $[3] = t7;
    } else {
      t7 = $[3];
    }
    const path = t7;
    if (!dropId || path.includes(dropId)) {
      t6 = path;
      break bb0;
    }
    let t8;
    if ($[4] !== dropId || $[5] !== path) {
      t8 = [...path, dropId];
      $[4] = dropId;
      $[5] = path;
      $[6] = t8;
    } else {
      t8 = $[6];
    }
    t6 = t8;
  }
  const currentDropEmbedPath = t6;
  let t7;
  if ($[7] !== dropSerialNo || $[8] !== quotePath || $[9] !== waveId) {
    bb1: {
      const path_0 = quotePath ? [...quotePath] : [];
      if (dropSerialNo === undefined) {
        t7 = path_0;
        break bb1;
      }
      const currentQuoteKey = `${waveId}:${dropSerialNo}`;
      if (!path_0.includes(currentQuoteKey)) {
        path_0.push(currentQuoteKey);
      }
      t7 = path_0;
    }
    $[7] = dropSerialNo;
    $[8] = quotePath;
    $[9] = waveId;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const currentQuotePath = t7;
  let t8;
  if ($[11] !== contentPresentation || $[12] !== part.content) {
    t8 = contentPresentation === "quorumCompact" ? parseQuorumProposalMarkdown(part.content) : null;
    $[11] = contentPresentation;
    $[12] = part.content;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const compactProposal = t8;
  const waveWithLinkSettings = drop?.wave as ApiWaveMinWithChatLinkSettings | undefined;
  const editLinkRestrictionApplies = isChatLinkRestrictionApplicable({
    dropType: drop?.drop_type ?? ApiDropType.Chat,
    linksDisabled: waveWithLinkSettings?.links_disabled === true,
    isWaveAdmin: drop?.wave.authenticated_user_admin === true,
    isWaveCreator: areHandlesEqual(connectedProfile?.handle, waveWithLinkSettings?.wave_author_handle)
  });
  if (isEditing) {
    const t9 = part.content ?? "";
    const t10 = drop?.wave.authenticated_user_admin === true;
    const t11 = editLinkRestrictionApplies ? CHAT_LINK_RESTRICTION_MESSAGE : null;
    let t12;
    if ($[14] !== onSave) {
      t12 = (content, mentions, groups, waves) => {
        if (onSave) {
          onSave(content, mentions, groups, waves);
        }
      };
      $[14] = onSave;
      $[15] = t12;
    } else {
      t12 = $[15];
    }
    let t13;
    if ($[16] !== onCancel) {
      t13 = () => {
        if (onCancel) {
          onCancel();
        }
      };
      $[16] = onCancel;
      $[17] = t13;
    } else {
      t13 = $[17];
    }
    let t14;
    if ($[18] !== isSaving || $[19] !== mentionedGroups || $[20] !== mentionedUsers || $[21] !== mentionedWaves || $[22] !== t10 || $[23] !== t11 || $[24] !== t12 || $[25] !== t13 || $[26] !== t9 || $[27] !== wave.id) {
      t14 = <EditDropLexical initialContent={t9} initialMentions={mentionedUsers} initialGroupMentions={mentionedGroups} initialWaveMentions={mentionedWaves} canMentionAll={t10} waveId={wave.id} isSaving={isSaving} linkRestrictionMessage={t11} onSave={t12} onCancel={t13} />;
      $[18] = isSaving;
      $[19] = mentionedGroups;
      $[20] = mentionedUsers;
      $[21] = mentionedWaves;
      $[22] = t10;
      $[23] = t11;
      $[24] = t12;
      $[25] = t13;
      $[26] = t9;
      $[27] = wave.id;
      $[28] = t14;
    } else {
      t14 = $[28];
    }
    return t14;
  }
  let t9;
  if ($[29] !== compactProposal || $[30] !== currentDropEmbedPath || $[31] !== currentQuotePath || $[32] !== drop?.hide_link_preview || $[33] !== drop?.id || $[34] !== drop?.nft_links || $[35] !== embedDepth || $[36] !== linkPreviewToggleControl || $[37] !== maxEmbedDepth || $[38] !== mentionedGroups || $[39] !== mentionedUsers || $[40] !== mentionedWaves || $[41] !== onLinkCardActionsActiveChange || $[42] !== onQuoteClick || $[43] !== part.content || $[44] !== referencedNfts) {
    t9 = compactProposal ? <QuorumProposalCompactContent proposal={compactProposal} mentionedUsers={mentionedUsers} mentionedGroups={mentionedGroups} mentionedWaves={mentionedWaves} referencedNfts={referencedNfts} nftLinks={drop?.nft_links} onQuoteClick={onQuoteClick} currentDropId={drop?.id} hideLinkPreviews={drop?.hide_link_preview} embedPath={currentDropEmbedPath} quotePath={currentQuotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} linkPreviewToggleControl={linkPreviewToggleControl} onLinkCardActionsActiveChange={onLinkCardActionsActiveChange} /> : <DropPartMarkdownWithPropLogger mentionedUsers={mentionedUsers} mentionedGroups={mentionedGroups} mentionedWaves={mentionedWaves} referencedNfts={referencedNfts} nftLinks={drop?.nft_links} partContent={part.content} onQuoteClick={onQuoteClick} currentDropId={drop?.id} hideLinkPreviews={drop?.hide_link_preview} embedPath={currentDropEmbedPath} quotePath={currentQuotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} linkPreviewToggleControl={linkPreviewToggleControl} onLinkCardActionsActiveChange={onLinkCardActionsActiveChange} />;
    $[29] = compactProposal;
    $[30] = currentDropEmbedPath;
    $[31] = currentQuotePath;
    $[32] = drop?.hide_link_preview;
    $[33] = drop?.id;
    $[34] = drop?.nft_links;
    $[35] = embedDepth;
    $[36] = linkPreviewToggleControl;
    $[37] = maxEmbedDepth;
    $[38] = mentionedGroups;
    $[39] = mentionedUsers;
    $[40] = mentionedWaves;
    $[41] = onLinkCardActionsActiveChange;
    $[42] = onQuoteClick;
    $[43] = part.content;
    $[44] = referencedNfts;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  let t10;
  if ($[46] !== drop) {
    t10 = typeof drop?.updated_at === "number" && drop.updated_at !== drop.created_at && <div className="tw-mt-0.5 tw-text-[10px] tw-font-normal tw-leading-none tw-text-iron-500">(edited)</div>;
    $[46] = drop;
    $[47] = t10;
  } else {
    t10 = $[47];
  }
  let t11;
  if ($[48] !== t10 || $[49] !== t9) {
    t11 = <div>{t9}{t10}</div>;
    $[48] = t10;
    $[49] = t9;
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  let t12;
  if ($[51] !== currentDropEmbedPath || $[52] !== currentQuotePath || $[53] !== embedDepth || $[54] !== maxEmbedDepth || $[55] !== onLinkCardActionsActiveChange || $[56] !== onQuoteClick || $[57] !== part.quoted_drop || $[58] !== wave) {
    t12 = part.quoted_drop?.drop_id && <div className="tw-mt-1.5"><WaveDropQuoteWithDropId dropId={part.quoted_drop.drop_id} partId={part.quoted_drop.drop_part_id} maybeDrop={part.quoted_drop.drop ? {
        ...part.quoted_drop.drop,
        wave
      } : null} waveId={wave.id} onQuoteClick={onQuoteClick} embedPath={currentDropEmbedPath} quotePath={currentQuotePath} embedDepth={(embedDepth ?? 0) + 1} maxEmbedDepth={maxEmbedDepth} onLinkCardActionsActiveChange={onLinkCardActionsActiveChange} /></div>;
    $[51] = currentDropEmbedPath;
    $[52] = currentQuotePath;
    $[53] = embedDepth;
    $[54] = maxEmbedDepth;
    $[55] = onLinkCardActionsActiveChange;
    $[56] = onQuoteClick;
    $[57] = part.quoted_drop;
    $[58] = wave;
    $[59] = t12;
  } else {
    t12 = $[59];
  }
  let t13;
  if ($[60] !== t11 || $[61] !== t12) {
    t13 = <>{t11}{t12}</>;
    $[60] = t11;
    $[61] = t12;
    $[62] = t13;
  } else {
    t13 = $[62];
  }
  return t13;
};
export default WaveDropPartContentMarkdown;